import { Ban, CalendarRange, CheckCircle2, DoorClosed, DoorOpen, History, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { Menu } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { Tooltip } from '../../../../components/Tooltip';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { BudgetPlanModal } from '../components/BudgetPlanModal';
import { GoalSettingPanel } from '../components/GoalSettingPanel';
import { goalsApi } from '../budgetGoalsApi';
import { PpaRosterPanel } from '../components/PpaRosterPanel';
import { budgetPlansApi } from '../budgetPlansApi';
import { BUDGET_PLAN_STATUS, type BudgetPlanDetail } from '../budgetPlanTypes';
import { ppasApi } from '../ppasApi';
import type { BudgetPlanDepartmentRow } from '../ppaTypes';

type TabKey = 'info' | 'goals' | 'ppas';

/**
 * One budget plan — the fiscal year's frame and its Goal Setting.
 *
 * On the **header** layout. A plan reads as one continuous thing: a frame, the goals it
 * adopts, and the trail of who moved it through the cycle — not a record whose content splits
 * into panels a reader chooses between. The Goal Setting earns a second tab rather than a
 * card because it is a LIST that grows, and a card of forty goals is a card nobody can scan.
 */
export function BudgetPlanProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<BudgetPlanDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [running, setRunning] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  /*
    LANDS ON PPAs — product call, 06 Oct 2026: "in the budget plan profile, the default nav is
    the PPA tab". A plan is opened to see who has proposed what, which is the roster, not to
    check its frame. `Info` is still the tab DECLARED first, so the strip's order and the
    shell's development check are untouched — only where the page opens.
  */
  const [tab, setTab] = useState<TabKey>('ppas');

  /*
    THE ROSTER IS FETCHED EAGERLY, with the record — not lazily when its tab opens.

    That is the profile's own two-tier rule read correctly rather than skipped: the header
    states how many departments have started out of how many are enrolled, and the tab badge
    states the same figure, so the roster IS the count. One fetch, one derivation, and the
    two surfaces cannot disagree. What stays lazy is a DEPARTMENT's own PPAs, which the panel
    loads when the reader drills into one.

    It fails SOFT. A reader of plans holds `budget-plans.read` and so may read the roster, but
    a failed request must not take the Info tab down with it — the tab then reads empty and
    says so, exactly as an empty roster does.
  */
  const [roster, setRoster] = useState<BudgetPlanDepartmentRow[]>([]);

  /*
    WHETHER THE PROGRAMME HAS ANY GOAL AT ALL — what the Open-submission button is gated on.

    It used to read the plan's own adopted count, and a plan adopts nothing now: the server
    refuses to open a window while the CATALOG is empty, because a PPA form whose goal picker
    has nothing in it is what that guard exists to prevent. The button has to be able to say
    the same thing, or the reader presses it and meets a refusal they could have been shown.

    Seeded TRUE — not empty — for the reason every prerequisite is: flashing "set up the goals
    first" on every load of a working plan is worse than a banner arriving a moment late, and
    a request that did not come back is not evidence that a catalog is empty.
  */
  const [catalogEmpty, setCatalogEmpty] = useState(false);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      budgetPlansApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setDetail(next);

          // Cleared on success, or a failed transition would keep its banner over a page that
          // has since re-read the record and disagrees with it.
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this plan.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });

      ppasApi
        .roster(id, signal)
        .then((rows) => {
          if (!signal?.aborted) setRoster(rows);
        })
        .catch(() => {
          // Soft: the roster is one tab, and a failed fetch must not blank the record.
          if (!signal?.aborted) setRoster([]);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /*
    Its own fetch with its own lifetime, not a field on the plan: the catalog belongs to no
    plan, so putting a count of it on this record's payload would be a figure that is the same
    on every row of the list. It FAILS SOFT — a failed probe leaves the button enabled and the
    server still refuses, which is the right way round.
  */
  useEffect(() => {
    const controller = new AbortController();

    goalsApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setCatalogEmpty(rows.length === 0);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [detail?.plan.id]);

  /** One runner for all three lifecycle actions — they differ only in which call they make. */
  async function run(action: (planId: string) => Promise<unknown>, failure: string) {
    if (!id) return;

    setRunning(true);
    setError(null);

    try {
      await action(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(failure, 0));
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Budget Plans', to: '/procurement/investment-planning/budget-plans' },
    detail?.plan.planNumber ?? 'Budget plan',
  ];

  // Guarded on having NOTHING to show, never on `error` alone: once a record is up it is
  // still the last good answer, and a refused transition goes to the banner.
  if (error && !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { plan } = detail;

  /*
    STARTED out of ENROLLED, derived from the roster rows the page already holds — the
    reference's own `0/60`. A department that has filed nothing is a row reading Not started
    and still counts toward the denominator, which is the whole reason the roster lists every
    enrolled office rather than only the ones that proposed.
  */
  const departmentCount = roster.length;
  const startedCount = roster.filter((row) => row.ppaCount > 0).length;
  const proposedTotal = roster.reduce((sum, row) => sum + row.total, 0);

  /*
    The Edit affordance, in the two shapes a card can take it.

    EDITABLE: the card's own `onEdit` under `editPermission`, which is also what the empty
    banner's button calls — one way to change a section, offered twice in the same card rather
    than invented a second time.

    NOT EDITABLE: a disabled menu entry carrying the reason. Disabled for STATE, never hidden —
    hiding is what a permission gate does, and a reader who may edit plans needs to know why
    they cannot edit THIS one. The empty banner then has no button, which is honest: once the
    window has closed there is no way to fill the section in.
  */
  const lockedReason = `This plan is ${plan.statusLabel.toLowerCase()}, so its frame can no longer be changed.`;

  const editProps = plan.isEditable
    ? { onEdit: () => setEditing(true), editPermission: 'budget-plans.update' }
    : {
        menuItems: [
          {
            key: 'edit',
            label: 'Edit',
            permission: 'budget-plans.update',
            disabledReason: lockedReason,
            onClick: () => undefined,
          },
        ] satisfies MenuItem[],
      };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="budget-plan-profile"
        header={
          <ProfileHeader
            id="budget-plan-profile-header"
            icon={<CalendarRange className="h-6 w-6" aria-hidden="true" />}
            title={plan.planNumber}
            meta={[
              `FY ${plan.fiscalYear}`,
              plan.statusLabel,
            ]}
            secondary={[
              `Submission by ${formatDateOnly(plan.submissionClosesAt)}`,
              `Covers ${formatDateOnly(plan.coverageStartDate)} – ${formatDateOnly(plan.coverageEndDate)}`,
              /*
                The reference's own `0/60`, and the plan's proposed total beside it. Both are
                RECORD-LEVEL facts — true on every tab — which is what the secondary line is
                for, and they are derived from the one roster the page holds rather than
                computed a second time inside the tab.
              */
              `${startedCount}/${departmentCount} departments started`,
              `${formatMoney(proposedTotal)} proposed`,
            ]}
            /*
              The pill is the ROW's state — live or deleted — and the lifecycle stage is in the
              meta line beside the year. Two pills would put one word against two meanings, and
              the disciplinary-record panel already settles which of them gets the pill.
            */
            status={
              <StatusText tone={plan.isActive ? 'active' : 'inactive'}>
                {plan.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={plan.createdByName}
            createdAt={plan.createdAt}
            /*
              THE RECORD-LEVEL COG, and History Logs is why it exists.

              A profile puts History Logs in its own header cog, scoped to the record — which
              is also this product's answer to the reference's LIFECYCLE timeline. That panel
              lists who moved the plan through each stage and when; here every one of those
              transitions is already an audit row, so the trail is read from the one
              append-only place rather than from a second history this module would have to
              write and keep in step.

              It sits in the HEADER rather than a panel because it is about the whole record:
              the header is on screen for every tab, and an action about the record must not
              be somewhere the reader has to navigate back to.
            */
            actions={
              <Menu
                id="budget-plan-profile-settings"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    onClick: () => setHistoryOpen(true),
                    permission: 'audit-logs.read',
                  },
                ]}
              />
            }
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="budget-plan-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : !plan.isActive ? (
            <ProfileBanner
              id="budget-plan-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`This plan is deleted. Its Goal Setting is kept, and FY ${plan.fiscalYear} is free for another plan — so a restore is refused if one has taken it.`}
              action={
                <PermissionGate permission="budget-plans.delete">
                  <Button
                    id="budget-plan-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void run(budgetPlansApi.activate, 'Could not restore this plan.')}
                    disabled={running}
                    loading={running}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : plan.status === BUDGET_PLAN_STATUS.draft ? (
            /*
              EVERY state gets a banner, not only the ones that are wrong — a banner that
              appears only when something needs attention trains the reader to skip the strip.
              The lifecycle action lives here and nowhere else: it acts on the whole record
              rather than on one card's fields.
            */
            <ProfileBanner
              id="budget-plan-profile-banner"
              tone="slate"
              icon={<DoorClosed className="h-4 w-4" aria-hidden="true" />}
              message={
                catalogEmpty
                  ? 'This plan is a draft, and there are no goals to align a PPA to. Set up the Goal Setting — sector, sub-sector, goal — before opening the submission window.'
                  : 'This plan is a draft. Departments cannot submit against it until its submission window is open.'
              }
              action={
                <PermissionGate permission="budget-plans.manage-submission">
                  {/*
                    Disabled for STATE, and the reason rides in the shared `<Tooltip>` — every
                    disabled control says why, and `<Button>` has no `disabledReason` of its
                    own. `block` because the child is a block inside the banner's action slot.
                  */}
                  <Tooltip
                    disabledReason={
                      catalogEmpty
                        ? 'Set up the Goal Setting first — a PPA has nothing to align to.'
                        : undefined
                    }
                    block
                  >
                    <Button
                      id="budget-plan-profile-open-submission"
                      variant="secondary"
                      leading={<DoorOpen className="h-4 w-4" aria-hidden="true" />}
                      onClick={() =>
                        void run(budgetPlansApi.openSubmission, 'Could not open the submission window.')
                      }
                      disabled={running || catalogEmpty}
                      loading={running}
                      loadingLabel="Opening"
                    >
                      Open submission
                    </Button>
                  </Tooltip>
                </PermissionGate>
              }
            />
          ) : plan.status === BUDGET_PLAN_STATUS.submissionOpen ? (
            <ProfileBanner
              id="budget-plan-profile-banner"
              tone="slate"
              icon={<DoorOpen className="h-4 w-4" aria-hidden="true" />}
              message={`The submission window is open. Departments may file their PPAs against this plan's goals until ${formatDateOnly(plan.submissionClosesAt)}.`}
              action={
                <PermissionGate permission="budget-plans.manage-submission">
                  <Button
                    id="budget-plan-profile-close-submission"
                    variant="secondary"
                    leading={<DoorClosed className="h-4 w-4" aria-hidden="true" />}
                    onClick={() =>
                      void run(budgetPlansApi.closeSubmission, 'Could not close the submission window.')
                    }
                    disabled={running}
                    loading={running}
                    loadingLabel="Closing"
                  >
                    Close submission
                  </Button>
                </PermissionGate>
              }
            />
          ) : (
            <ProfileBanner
              id="budget-plan-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={`The submission window has closed. This plan is ${plan.statusLabel.toLowerCase()}, so its frame and Goal Setting can no longer be edited — Budget Review reads them as they stand.`}
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          { key: 'goals', label: 'Goal Setting' },
          {
            /*
              ONE NUMBER, through `<CountBadge>` — never inline `(N)` parens, and never a
              PAIR. It read `PPAs (0/3)`, which broke the folder-tab rule twice over: the
              parens are the form that rule names outright, and the denominator is already
              stated three inches above on the header's secondary line as
              `0/3 departments started`. The same value in two slots, on one screen.

              The number is STARTED, not the roster's length — how many offices exist is what
              the reader sees the moment they open the tab, where whether anything is waiting
              for them is not. A zero renders, which is what says the tab is empty before it
              is clicked.
            */
            key: 'ppas',
            label: (
              <>
                PPAs <CountBadge count={startedCount} />
              </>
            ),
            // The ROSTER is part of reading a plan, so the tab is not gated on `ppas.read`:
            // a reader of plans sees who is expected to propose without a second grant.
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
        // The PPA tab is a table that owns its own toolbar and pager, so it needs the panel
        // edge to edge; a stack of cards would cap it to a readable measure it does not want.
        panel={tab === 'info' ? 'cards' : 'bleed'}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard id="budget-plan-details" title="Details" {...editProps}>
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Plan number', value: plan.planNumber, mono: true },
                  { label: 'Fiscal year', value: String(plan.fiscalYear), mono: true },
                  { label: 'Stage', value: plan.statusLabel },
                  {
                    label: 'Submission deadline',
                    value: formatDateOnly(plan.submissionClosesAt),
                    mono: true,
                  },
                  { label: 'Coverage start', value: formatDateOnly(plan.coverageStartDate), mono: true },
                  { label: 'Coverage end', value: formatDateOnly(plan.coverageEndDate), mono: true },
                  { label: 'Description', value: plan.description },
                  { label: 'Notes', value: plan.notes },
                ]}
              />
            </ProfileCard>

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id="budget-plan-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: plan.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(plan.createdAt)} className="font-mono">
                        {formatDateTime(plan.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: plan.updatedByName },
                  {
                    label: 'Updated',
                    value: plan.updatedAt ? (
                      <time dateTime={toIso(plan.updatedAt)} className="font-mono">
                        {formatDateTime(plan.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : tab === 'ppas' ? (
          <PpaRosterPanel detail={detail} roster={roster} onChanged={() => load()} />
        ) : (
          /*
            THE CATALOG ITSELF — sector, sub-sector, goal — not this plan's selection.

            It was a `<PreviewGrid>` of the goals the plan had adopted, and the card's cog
            opened the PLAN's own edit form, so one modal held the fiscal year, the window, the
            coverage AND the goals. Two defects with one cause: "one card, one modal" broken
            from both ends, and a create process that collected a framing the reference's own
            form never asks for.

            The reference draws this as a TREE-GRID whose every row adds its own child, and its
            comment says what that replaced: "this tab is the catalog's home — it no longer
            defers to Settings › Goals."

            NO `<ProfileCard>` around it. A card is a stack of facts with one cog and one Edit;
            this is a surface that creates, corrects, deletes and restores at three rungs, and
            a card holding it would owe an Edit that could not mean anything.
          */
          <GoalSettingPanel />
        )}
      </ProfileHeaderShell>

      {editing ? (
        <BudgetPlanModal
          open
          editing={detail}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      {/*
        Scoped by `entityId` ALONE, with no `entityTypes` beside it — "scope by ID or by
        TYPE, never both". Adding the type bound only subtracts: a write the server REFUSED
        never reached an entity, so it carries no entityType, and a refused transition is
        exactly the event a reader opens a plan's history to find.

        The Goal Setting links come through on the same scope, because the audit row's
        ParentEntityId resolves to the plan from the route that wrote them.
      */}
      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="budget-plans"
        entityId={plan.id}
      />
    </div>
  );
}
